import { useEffect, useRef, useState } from "react";
import Player from "@vimeo/player";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";

interface VimeoPlayerProps {
  videoId: string;
  title?: string;
  initialProgress?: {
    seconds_watched: number;
    percent_watched: number;
    last_position_sec: number;
    furthest_watched_sec?: number;
    completed_at: string | null;
  };
  onProgressUpdate?: (progressData: {
    seconds_watched: number;
    percent_watched: number;
    last_position_sec: number;
    furthest_watched_sec: number;
    duration: number;
  }) => void;
  onImmediateProgressUpdate?: (percent: number) => void;
  onLessonComplete?: () => void;
}

export function VimeoPlayer({
  videoId,
  title,
  initialProgress,
  onProgressUpdate,
  onImmediateProgressUpdate,
  onLessonComplete,
}: VimeoPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  

  // Use refs to store callback functions and avoid recreating player
  const onProgressUpdateRef = useRef(onProgressUpdate);
  const onImmediateProgressUpdateRef = useRef(onImmediateProgressUpdate);
  const onLessonCompleteRef = useRef(onLessonComplete);
  const lastUpdateRef = useRef<number>(0);
  const updateIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const durationRef = useRef<number>(0);
  const furthestWatchedRef = useRef<number>(
    initialProgress?.furthest_watched_sec || initialProgress?.last_position_sec || 0
  );
  const isInitializingRef = useRef<boolean>(true);
  const isSeekingRef = useRef<boolean>(false);
  const lastNaturalPositionRef = useRef<number>(0);

  // Update refs when props change
  useEffect(() => {
    onProgressUpdateRef.current = onProgressUpdate;
  }, [onProgressUpdate]);

  useEffect(() => {
    onImmediateProgressUpdateRef.current = onImmediateProgressUpdate;
  }, [onImmediateProgressUpdate]);

  useEffect(() => {
    onLessonCompleteRef.current = onLessonComplete;
  }, [onLessonComplete]);


  // Only recreate player when videoId changes
  useEffect(() => {
    if (!containerRef.current || !videoId) return;

    // Create the player
    const player = new Player(containerRef.current, {
      id: parseInt(videoId),
      width: 800,
      responsive: true,
      title: false,
    });

    playerRef.current = player;

    // Set up event listeners
    player.ready().then(async () => {
      setIsLoading(false);

      // Get video duration
      try {
        const videoDuration = await player.getDuration();
        durationRef.current = videoDuration;

        // Initialize furthest watched percentage
        const initialFurthest = initialProgress?.furthest_watched_sec || initialProgress?.last_position_sec || 0;
        const initialFurthestPercent = parseInt(((initialFurthest / videoDuration) * 100).toString());
        
        // Immediately send initial progress to sidebar (clamped to 100)
        onImmediateProgressUpdateRef.current?.(Math.min(initialFurthestPercent, 100));
        
        // Resume from last position if there's progress
        if (initialProgress && initialProgress.last_position_sec > 10) {
          await player.setCurrentTime(initialProgress.last_position_sec);
        }
      } catch (error) {
        console.error("Error setting up player:", error);
      }

      // Set up event listeners after player is ready
      player.on("timeupdate", async (data) => {
        if (durationRef.current > 0) {
          // Mark initialization as complete after first timeupdate
          isInitializingRef.current = false;
          
          const currentFurthest = furthestWatchedRef.current;
          const gracePeriod = 2; // Small grace period for natural playback
          
          // CRITICAL: If current position is ahead of furthest watched (user skipped ahead), snap them back
          // Check this even during seeking to prevent any progress updates from skipped positions
          if (data.seconds > currentFurthest + gracePeriod) {
            // User somehow got ahead - snap them back immediately
            await player.setCurrentTime(currentFurthest);
            return; // Don't process this update
          }
          
          // Only update furthest position if user is naturally watching (not seeking)
          if (!isSeekingRef.current) {
            // Natural progression - only allow reasonable forward movement
            const maxNaturalJump = 3; // Max 3 seconds per timeupdate event
            const timeSinceLastNatural = Math.abs(data.seconds - lastNaturalPositionRef.current);
            
            if (timeSinceLastNatural <= maxNaturalJump && data.seconds > currentFurthest) {
              furthestWatchedRef.current = data.seconds;
              lastNaturalPositionRef.current = data.seconds;
            } else if (data.seconds <= currentFurthest) {
              // User is reviewing previous content - this is fine
              lastNaturalPositionRef.current = data.seconds;
            }
          } else {
            // Currently seeking - don't update furthest position, just track where we are
            lastNaturalPositionRef.current = data.seconds;
          }
          
          // Recalculate current furthest after potential update
          const updatedFurthest = furthestWatchedRef.current;
          
          // Calculate furthest watched percentage (based on furthest watched, not current position)
          // Only use furthest watched, never the current position which might be ahead due to seeking
          // Clamp to 100 to prevent exceeding 100% when player reports positions beyond duration
          const furthestPercent = Math.min(
            parseInt(((updatedFurthest / durationRef.current) * 100).toString()),
            100
          );

          // Send update to sidebar - always use furthest watched percentage, never current position
          onImmediateProgressUpdateRef.current?.(furthestPercent);

          const now = Date.now();

          // Throttle database saves to every 5 seconds
          // CRITICAL: Only save if position is valid (not ahead of furthest watched) and we're not seeking
          // This check happens after we've already snapped back if needed, so position should be valid
          const isPositionValid = data.seconds <= updatedFurthest + gracePeriod;
          
          if (now - lastUpdateRef.current > 5000 && !isSeekingRef.current && isPositionValid) {
            lastUpdateRef.current = now;

            // IMPORTANT: Progress updates should only use furthest_watched_sec, not current playback position
            // This prevents users from seeking to the end and updating progress
            const clampedFurthest = Math.min(updatedFurthest, durationRef.current);
            const progressData = {
              seconds_watched: clampedFurthest, // Use furthest watched, not current position
              percent_watched: furthestPercent / 100, // Use furthest watched percentage (already clamped to 100)
              last_position_sec: Math.min(data.seconds, clampedFurthest), // Don't save position ahead of furthest watched
              furthest_watched_sec: clampedFurthest,
              duration: durationRef.current,
            };

            // Debounce the save operation
            if (updateIntervalRef.current) {
              clearTimeout(updateIntervalRef.current);
            }

            updateIntervalRef.current = setTimeout(() => {
              onProgressUpdateRef.current?.(progressData);
            }, 1000);
          }
        }
      });

      // Prevent seeking beyond furthest watched position
      player.on("seeking", async (data) => {
        // Mark that we're currently seeking
        isSeekingRef.current = true;
        
        // Skip enforcement during initialization (resume position seeking)
        if (isInitializingRef.current) {
          return;
        }
        
        const currentFurthest = furthestWatchedRef.current;
        const gracePeriod = 2; // Very small grace period for minor seeking adjustments
        const maxSeekAhead = currentFurthest + gracePeriod;
        
        // Prevent seeking beyond the furthest watched position (with small grace period)
        // Also prevent seeking to the end of the video unless they've actually watched that far
        const nearEnd = durationRef.current - data.seconds < 5; // Within 5 seconds of end
        const hasWatchedNearEnd = durationRef.current - currentFurthest < 5; // Has watched within 5 seconds of end
        
        // If user tries to seek ahead of furthest watched or to the end without watching, snap them back
        if (data.seconds > maxSeekAhead || (nearEnd && !hasWatchedNearEnd)) {
          // Clear any pending progress updates to prevent saving invalid progress
          if (updateIntervalRef.current) {
            clearTimeout(updateIntervalRef.current);
            updateIntervalRef.current = null;
          }
          
          // User tried to jump too far ahead or to the end - snap them back immediately
          await player.setCurrentTime(currentFurthest);
        }
      });

      // Also handle seeked event to catch any seeks that complete
      player.on("seeked", async (data) => {
        // Skip enforcement during initialization
        if (isInitializingRef.current) {
          isSeekingRef.current = false;
          return;
        }
        
        const currentFurthest = furthestWatchedRef.current;
        const gracePeriod = 2;
        
        // Double-check position after seek completes - snap back if needed
        if (data.seconds > currentFurthest + gracePeriod) {
          // Clear any pending progress updates to prevent saving invalid progress
          if (updateIntervalRef.current) {
            clearTimeout(updateIntervalRef.current);
            updateIntervalRef.current = null;
          }
          
          await player.setCurrentTime(currentFurthest);
        }
        
        // Clear seeking flag after a delay to allow timeupdate to process
        setTimeout(() => {
          isSeekingRef.current = false;
        }, 1000);
      });

      player.on("ended", () => {
        // Only mark as complete if user has actually watched to the end
        // Check if furthest watched is within 5 seconds of the end
        const hasWatchedToEnd = durationRef.current - furthestWatchedRef.current < 5;
        
        if (hasWatchedToEnd) {
          // Update furthest watched to the end
          furthestWatchedRef.current = durationRef.current;
          onImmediateProgressUpdateRef.current?.(100);
          onLessonCompleteRef.current?.();
        } else {
          // User skipped to the end - don't mark as complete, snap them back
          player.setCurrentTime(furthestWatchedRef.current).catch(() => {
            // Ignore errors if player is already destroyed
          });
        }
      });
    });

    // Cleanup
    return () => {
      if (updateIntervalRef.current) {
        clearTimeout(updateIntervalRef.current);
      }
      player.destroy();
    };
  }, [videoId]);

  return (
    <div className="space-y-4">
      {/* Progress indicator - moved above video */}
      {/* {!isLoading && furthestWatchedPercent > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isCompleted && <CheckCircle2 className="h-4 w-4 text-green-500" />}
              <span className="text-sm font-medium text-foreground">
                {isCompleted ? "Lesson Completed" : `${Math.round(furthestWatchedPercent)}% watched`}
              </span>
            </div>
            <span className="text-xs text-muted-foreground">
              {Math.round(furthestWatchedPercent)}%
            </span>
          </div>
          <Progress
            value={Math.min(furthestWatchedPercent, 100)}
            className="h-2"
          />
        </div>
      )} */}

      <Card className="overflow-hidden">
        <div className="relative aspect-video bg-black">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
          )}
          <div ref={containerRef} className="w-full h-full" />
        </div>
        {title && (
          <div className="p-4 border-t">
            <h3 className="font-semibold text-lg text-foreground">{title}</h3>
          </div>
        )}
      </Card>
    </div>
  );
}
